import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
  IconLayoutDashboard,
  IconBuildingSkyscraper,
  IconUsers,
  IconTargetArrow,
  IconLayoutKanban,
  IconFileDescription,
  IconBusinessplan,
  IconCalendarEvent,
  IconFileCertificate,
  IconClipboardCheck,
  IconArrowsExchange,
  IconUsersGroup,
  IconLink,
  IconCoins,
  IconWallet,
  IconCash,
  IconBolt,
  IconChartBar,
  IconChartPie,
  IconTarget,
  IconSettings,
  IconBell,
  IconChevronRight,
  IconChevronLeft,
  IconGavel,
  IconBriefcase,
  IconRocket,
  IconBuildingCommunity,
  IconUserPlus,
  IconHeadset,
  IconBuildingStore,
  type Icon as TablerIcon,
} from "@tabler/icons-react";
import { Logo } from "@/components/ui/logo";
import { cn } from "@/lib/utils/cn";

type ItemTone = "brand" | "warning" | "success" | "info" | "pink" | "orange";

interface BaseItem {
  key: string;
  icon: TablerIcon;
  tone?: ItemTone;
}

interface LeafItem extends BaseItem {
  to: string;
  badge?: string | number;
  children?: never;
}

interface ParentItem extends BaseItem {
  to?: never;
  badge?: never;
  children: LeafItem[];
}

type Item = LeafItem | ParentItem;
type Group = { key: string; items: Item[] };

const groups: Group[] = [
  {
    key: "overview",
    items: [{ to: "/dashboard", key: "dashboard", icon: IconLayoutDashboard, tone: "brand" }],
  },
  {
    key: "operation",
    items: [
      { to: "/people", key: "people", icon: IconUsers, tone: "info" },
      { to: "/properties", key: "properties", icon: IconBuildingSkyscraper, tone: "brand" },
      { to: "/proposals", key: "proposals", icon: IconFileDescription, tone: "info" },
      { to: "/deals", key: "deals", icon: IconBusinessplan, tone: "success" },
      { to: "/demands", key: "demands", icon: IconTarget, tone: "pink" },
      { to: "/agenda", key: "agenda", icon: IconCalendarEvent, tone: "warning" },
      { to: "/leads", key: "leads", icon: IconTargetArrow, tone: "orange" },
      { to: "/pipeline", key: "pipeline", icon: IconLayoutKanban, tone: "pink" },
    ],
  },
  {
    key: "core",
    items: [
      {
        key: "launches",
        icon: IconRocket,
        tone: "orange",
        children: [
          { to: "/launches", key: "launchUnits", icon: IconBuildingCommunity, tone: "brand" },
          {
            to: "/launches/proposals",
            key: "launchProposals",
            icon: IconFileDescription,
            tone: "info",
          },
        ],
      },
      {
        key: "legal",
        icon: IconGavel,
        tone: "info",
        children: [
          { to: "/contracts", key: "contracts", icon: IconFileCertificate, tone: "info" },
          { to: "/inspections", key: "inspections", icon: IconClipboardCheck, tone: "brand" },
        ],
      },
      {
        key: "cooperation",
        icon: IconBriefcase,
        tone: "pink",
        children: [
          { to: "/parceiros", key: "partners", icon: IconUsersGroup, tone: "pink" },
          { to: "/cobroking", key: "cobroking", icon: IconArrowsExchange, tone: "pink" },
          { to: "/showcases", key: "showcases", icon: IconBuildingStore, tone: "orange" },
          { to: "/listings", key: "listings", icon: IconLink, tone: "info" },
          { to: "/referrals", key: "referrals", icon: IconUserPlus, tone: "success" },
        ],
      },
      {
        key: "analysis",
        icon: IconChartPie,
        tone: "brand",
        children: [
          { to: "/commissions", key: "commissions", icon: IconCoins, tone: "warning" },
          { to: "/wallet", key: "wallet", icon: IconWallet, tone: "success" },
          { to: "/goals", key: "goals", icon: IconTarget, tone: "pink" },
          { to: "/financial", key: "financial", icon: IconCash, tone: "success" },
          { to: "/automation", key: "automation", icon: IconBolt, tone: "warning" },
          { to: "/reports", key: "reports", icon: IconChartBar, tone: "info" },
        ],
      },
    ],
  },
  {
    key: "system",
    items: [
      { to: "/notifications", key: "notifications", icon: IconBell, tone: "brand" },
      { to: "/support", key: "support", icon: IconHeadset, tone: "info" },
      { to: "/settings", key: "settings", icon: IconSettings, tone: "brand" },
    ],
  },
];

const toneActiveBg: Record<ItemTone, string> = {
  brand: "bg-gradient-to-r from-brand-500/25 to-brand-500/5",
  info: "bg-gradient-to-r from-info/25 to-info/5",
  success: "bg-gradient-to-r from-success/25 to-success/5",
  warning: "bg-gradient-to-r from-warning/30 to-warning/5",
  pink: "bg-gradient-to-r from-pink/25 to-pink/5",
  orange: "bg-gradient-to-r from-orange/25 to-orange/5",
};

const toneIcon: Record<ItemTone, string> = {
  brand: "bg-brand-500/15 text-brand-300",
  info: "bg-info/15 text-purple-300",
  success: "bg-success/15 text-emerald-300",
  warning: "bg-warning/15 text-amber-300",
  pink: "bg-pink/15 text-pink-300",
  orange: "bg-orange/15 text-orange-300",
};

const toneIconActive: Record<ItemTone, string> = {
  brand: "bg-brand-grad text-white shadow-glow",
  info: "bg-gradient-to-br from-info to-brand-500 text-white shadow-glow-purple",
  success: "bg-argon-grad-success text-white",
  warning: "bg-gradient-to-br from-warning to-orange text-white",
  pink: "bg-argon-grad-warm text-white shadow-glow-pink",
  orange: "bg-gradient-to-br from-orange to-pink text-white",
};

function isParent(item: Item): item is ParentItem {
  return "children" in item && Array.isArray(item.children);
}

export function Sidebar({
  onItemClick,
  collapsed = false,
  onToggleCollapse,
}: {
  onItemClick?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();

  const initialOpen = useMemo(() => {
    const open = new Set<string>();
    for (const g of groups) {
      for (const it of g.items) {
        if (isParent(it) && it.children.some((c) => location.pathname.startsWith(c.to))) {
          open.add(it.key);
        }
      }
    }
    return open;
  }, [location.pathname]);

  const [openParents, setOpenParents] = useState<Set<string>>(initialOpen);

  useEffect(() => {
    setOpenParents((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const g of groups) {
        for (const it of g.items) {
          if (isParent(it) && it.children.some((c) => location.pathname.startsWith(c.to))) {
            if (!next.has(it.key)) {
              next.add(it.key);
              changed = true;
            }
          }
        }
      }
      return changed ? next : prev;
    });
  }, [location.pathname]);

  function toggleParent(key: string) {
    setOpenParents((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <TooltipPrimitive.Provider delayDuration={200}>
      <aside
        className={cn(
          "relative flex h-full flex-col overflow-hidden bg-sidebar-grad text-slate-200 shadow-[8px_0_24px_-12px_rgba(8,12,32,0.55)] transition-[width] duration-300 ease-out",
          collapsed ? "w-[60px]" : "w-[228px]",
        )}
      >
        {/* Sutil fade no canto direito pra suavizar transição visual com o main */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-20 top-0 h-72 w-72 rounded-full bg-brand-500/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-20 -right-10 h-64 w-64 rounded-full bg-pink/10 blur-3xl"
        />

        <div
          className={cn(
            "relative z-10 flex items-center border-b border-white/5 py-3",
            collapsed ? "justify-center px-2" : "justify-between gap-3 px-5",
          )}
        >
          {collapsed ? (
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-argon-grad-cool text-[12px] font-bold text-white shadow-glow">
              IG
            </div>
          ) : (
            <>
              <Logo invert />
              <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white/60 backdrop-blur">
                v2
              </span>
            </>
          )}
        </div>

        {/* Collapse toggle button (desktop only) */}
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expandir sidebar" : "Recolher sidebar"}
            title={collapsed ? "Expandir" : "Recolher"}
            className="absolute -right-3 top-[22px] z-20 hidden h-6 w-6 items-center justify-center rounded-full border border-white/15 bg-gradient-to-br from-sidebar to-sidebar-muted text-white/80 shadow-[0_4px_14px_-2px_rgba(8,12,32,0.5)] ring-1 ring-black/20 transition-all duration-200 hover:scale-105 hover:border-brand-400 hover:bg-brand-grad hover:text-white hover:shadow-glow lg:flex"
          >
            {collapsed ? <IconChevronRight size={13} /> : <IconChevronLeft size={13} />}
          </button>
        )}

        <nav
          className={cn(
            "scrollbar-none relative z-10 flex-1 overflow-y-auto py-3",
            collapsed ? "px-2" : "px-3",
          )}
        >
          {groups.map((group) => (
            <div key={group.key} className="mb-2 last:mb-0">
              {!collapsed && group.key !== "overview" && (
                <p className="px-3 pb-1.5 pt-2 text-[9.5px] font-bold uppercase tracking-[0.12em] text-slate-500">
                  {t(`nav.groups.${group.key}`)}
                </p>
              )}
              {collapsed && (
                <div className="my-2 h-px bg-white/5 first:hidden" aria-hidden />
              )}
              <ul className="flex flex-col gap-0.5">
                {group.items.map((item) =>
                  isParent(item) ? (
                    <ParentRow
                      key={item.key}
                      item={item}
                      open={openParents.has(item.key)}
                      onToggle={() => toggleParent(item.key)}
                      onItemClick={onItemClick}
                      collapsed={collapsed}
                    />
                  ) : (
                    <LeafRow
                      key={item.to}
                      item={item}
                      onItemClick={onItemClick}
                      collapsed={collapsed}
                    />
                  ),
                )}
              </ul>
            </div>
          ))}
        </nav>

      </aside>
    </TooltipPrimitive.Provider>
  );
}

function LeafRow({
  item,
  onItemClick,
  indented = false,
  collapsed = false,
}: {
  item: LeafItem;
  onItemClick?: () => void;
  indented?: boolean;
  collapsed?: boolean;
}) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const tone = item.tone ?? "brand";
  const label = t(`nav.items.${item.key}`);

  return (
    <li>
      <Tooltip label={label} disabled={!collapsed}>
        <NavLink
          to={item.to}
          onClick={onItemClick}
          className={({ isActive }) =>
            cn(
              "group relative flex items-center rounded-lg py-1.5 text-[12.5px] font-medium transition-all",
              collapsed ? "justify-center px-1.5" : "gap-3 px-2.5",
              !collapsed && indented && "pl-3",
              isActive
                ? cn(toneActiveBg[tone], "text-white shadow-inner-light")
                : "text-slate-300 hover:bg-white/[0.04] hover:text-white",
            )
          }
        >
          {({ isActive }) => (
            <>
              {isActive && !collapsed && (
                <span
                  aria-hidden
                  className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-white"
                />
              )}
              <span className="relative">
                <span
                  className={cn(
                    "flex shrink-0 items-center justify-center rounded-lg transition-all",
                    indented && !collapsed ? "h-6 w-6" : "h-8 w-8",
                    isActive ? toneIconActive[tone] : toneIcon[tone],
                  )}
                >
                  <Icon size={indented && !collapsed ? 13 : 16} />
                </span>
                {collapsed && item.badge !== undefined && (
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-gradient-to-br from-pink to-orange px-1 text-[9px] font-bold text-white shadow-glow-pink">
                    {item.badge}
                  </span>
                )}
              </span>
              {!collapsed && (
                <>
                  <span className="flex-1 truncate">{label}</span>
                  {item.badge !== undefined ? (
                    <span className="rounded-md bg-gradient-to-br from-pink to-orange px-1.5 py-0.5 text-[10px] font-bold text-white shadow-glow-pink">
                      {item.badge}
                    </span>
                  ) : (
                    isActive && (
                      <IconChevronRight
                        size={13}
                        className="text-white/60 transition-transform group-hover:translate-x-0.5"
                      />
                    )
                  )}
                </>
              )}
            </>
          )}
        </NavLink>
      </Tooltip>
    </li>
  );
}

function ParentRow({
  item,
  open,
  onToggle,
  onItemClick,
  collapsed = false,
}: {
  item: ParentItem;
  open: boolean;
  onToggle: () => void;
  onItemClick?: () => void;
  collapsed?: boolean;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const Icon = item.icon;
  const tone = item.tone ?? "brand";
  const childActive = item.children.some((c) => location.pathname.startsWith(c.to));
  const label = t(`nav.items.${item.key}`);

  // Collapsed → exibe como flyout popover ao clicar
  if (collapsed) {
    return (
      <li>
        <PopoverPrimitive.Root>
          <Tooltip label={label} disabled={false}>
            <PopoverPrimitive.Trigger asChild>
              <button
                type="button"
                className={cn(
                  "group relative flex w-full items-center justify-center rounded-xl px-1.5 py-2 transition-all",
                  childActive
                    ? cn(toneActiveBg[tone], "text-white shadow-inner-light")
                    : "text-slate-300 hover:bg-white/[0.04] hover:text-white",
                )}
              >
                <span
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-lg transition-all",
                    childActive ? toneIconActive[tone] : toneIcon[tone],
                  )}
                >
                  <Icon size={16} />
                </span>
              </button>
            </PopoverPrimitive.Trigger>
          </Tooltip>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              side="right"
              sideOffset={8}
              align="start"
              className="z-50 w-56 rounded-xl border border-white/10 bg-sidebar-grad p-2 text-slate-200 shadow-card data-[state=open]:animate-fade-up"
            >
              <p className="px-2 pb-2 pt-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {label}
              </p>
              <ul className="flex flex-col gap-0.5">
                {item.children.map((c) => (
                  <LeafRow
                    key={c.to}
                    item={c}
                    onItemClick={onItemClick}
                    collapsed={false}
                  />
                ))}
              </ul>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          "group relative flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-[12.5px] font-medium transition-all",
          childActive
            ? cn(toneActiveBg[tone], "text-white shadow-inner-light")
            : "text-slate-300 hover:bg-white/[0.04] hover:text-white",
        )}
      >
        {childActive && (
          <span
            aria-hidden
            className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-white"
          />
        )}
        <span
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all",
            childActive ? toneIconActive[tone] : toneIcon[tone],
          )}
        >
          <Icon size={16} />
        </span>
        <span className="flex-1 truncate text-left">{label}</span>
        <IconChevronRight
          size={13}
          className={cn(
            "text-slate-400 transition-transform",
            open ? "rotate-90 text-white" : "rotate-0",
          )}
        />
      </button>

      {open && (
        <ul className="relative mt-0.5 ml-4 flex flex-col gap-0.5 border-l border-white/10 pl-2">
          {item.children.map((child) => (
            <LeafRow key={child.to} item={child} onItemClick={onItemClick} indented />
          ))}
        </ul>
      )}
    </li>
  );
}

function Tooltip({
  label,
  disabled,
  children,
}: {
  label: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  if (disabled) return <>{children}</>;
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side="right"
          sideOffset={8}
          className="z-50 rounded-md border border-white/10 bg-sidebar px-2.5 py-1.5 text-[12px] font-semibold text-white shadow-card data-[state=delayed-open]:animate-fade-in"
        >
          {label}
          <TooltipPrimitive.Arrow className="fill-sidebar" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
